import { useState } from 'react';
import { History, Search, ShieldAlert } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
} from '@/components/ui';
import {
  DESTRUCTIVE_ACTIONS,
  useAuditFilters,
  useAuditLog,
  type AuditAction,
  type AuditEntry,
} from '@/lib/audit-api';
import { humanize, statusTone } from '@/lib/status';
import { errorMessage, formatDateTime, formatNumber } from '@/lib/utils';

/**
 * The audit trail (spec §49).
 *
 * Read-only, and visibly so: there is no control on this screen that changes a
 * row, because the trail is append-only on the server (rule 4). A log somebody
 * can tidy up is not evidence of anything.
 *
 * The filters are built from the values actually present in the table rather
 * than from the `AuditAction` enum — offering twenty-one actions when four have
 * ever been recorded sends the reader down seventeen empty filters.
 */

interface Filters {
  search: string;
  action: AuditAction | '';
  entity: string;
  actorId: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { search: '', action: '', entity: '', actorId: '', from: '', to: '' };

export function AuditLogsPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const options = useAuditFilters();
  const log = useAuditLog({
    page,
    limit: 25,
    search: filters.search,
    ...(filters.action ? { action: filters.action } : {}),
    ...(filters.entity ? { entity: filters.entity } : {}),
    ...(filters.actorId ? { actorId: filters.actorId } : {}),
    from: filters.from,
    to: filters.to,
  });

  const update = (patch: Partial<Filters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const filtered = Object.values(filters).some(Boolean);

  return (
    <div>
      <PageHeader
        title="Audit Logs"
        description="Every sensitive action, who performed it, and what changed."
      />

      <Card className="mb-4 p-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Field label="Search" htmlFor="audit-search" className="xl:col-span-2">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
                aria-hidden="true"
              />
              <Input
                id="audit-search"
                value={filters.search}
                onChange={(event) => update({ search: event.target.value })}
                placeholder="Description, actor email or record id"
                className="pl-9"
              />
            </div>
          </Field>

          <Field label="Action" htmlFor="audit-action">
            <Select
              id="audit-action"
              value={filters.action}
              onChange={(event) => update({ action: event.target.value as AuditAction | '' })}
            >
              <option value="">Every action</option>
              {options.data?.actions.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {humanize(entry.value)} ({formatNumber(entry.count)})
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Record type" htmlFor="audit-entity">
            <Select
              id="audit-entity"
              value={filters.entity}
              onChange={(event) => update({ entity: event.target.value })}
            >
              <option value="">Everything</option>
              {options.data?.entities.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.value} ({formatNumber(entry.count)})
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Performed by" htmlFor="audit-actor" className="xl:col-span-2">
            <Select
              id="audit-actor"
              value={filters.actorId}
              onChange={(event) => update({ actorId: event.target.value })}
            >
              <option value="">Anyone</option>
              {options.data?.actors.map((actor) => (
                <option key={actor.id} value={actor.id}>
                  {actor.email ?? actor.id} ({formatNumber(actor.count)})
                </option>
              ))}
            </Select>
          </Field>

          <Field label="From" htmlFor="audit-from">
            <Input
              id="audit-from"
              type="date"
              value={filters.from}
              onChange={(event) => update({ from: event.target.value })}
            />
          </Field>

          <Field label="To" htmlFor="audit-to" hint="Inclusive — the whole of that day.">
            <Input
              id="audit-to"
              type="date"
              value={filters.to}
              onChange={(event) => update({ to: event.target.value })}
            />
          </Field>
        </div>

        {filtered && (
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilters(EMPTY);
                setPage(1);
              }}
            >
              Clear filters
            </Button>
            <p className="text-xs text-ink-subtle">
              {formatNumber(log.data?.pagination.total ?? 0)} matching entries
            </p>
          </div>
        )}
      </Card>

      <Card>
        {log.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : log.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load the audit trail">
              {errorMessage(log.error)}
            </ErrorAlert>
          </div>
        ) : (log.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Nothing recorded here"
            description={
              filtered
                ? 'No entry matches these filters. Try widening the date range.'
                : 'Sensitive actions appear here as they happen.'
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>Who</Th>
                <Th>Action</Th>
                <Th>What happened</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {log.data?.items.map((entry) => (
                <tr key={entry.id} className="hover:bg-canvas/60">
                  <Td className="whitespace-nowrap text-xs text-ink-subtle">
                    {formatDateTime(entry.createdAt)}
                  </Td>
                  <Td>
                    <p className="text-sm text-ink">{entry.actor?.email ?? 'System'}</p>
                    {entry.actor?.role && (
                      <p className="text-xs text-ink-subtle">{humanize(entry.actor.role)}</p>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={actionTone(entry.action)}>{humanize(entry.action)}</Badge>
                  </Td>
                  <Td>
                    <p className="text-sm text-ink">{entry.summary}</p>
                    <p className="text-xs text-ink-subtle">
                      {entry.entity}
                      {entry.changes.length > 0 &&
                        ` · ${entry.changes.length} ${
                          entry.changes.length === 1 ? 'field' : 'fields'
                        } changed`}
                    </p>
                  </Td>
                  <Td className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelected(entry)}>
                      Details
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {log.data && log.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {formatNumber(log.data.pagination.total)} entries · page {log.data.pagination.page} of{' '}
              {formatNumber(log.data.pagination.totalPages)}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!log.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!log.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <p className="mt-3 flex items-center gap-1.5 text-xs text-ink-subtle">
        <History className="size-3.5" aria-hidden="true" />
        Append-only. Entries are never edited or deleted, here or anywhere else.
      </p>

      <AuditDrawer entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

/* ==========================================================================
   Detail drawer
   ========================================================================== */

function AuditDrawer({ entry, onClose }: { entry: AuditEntry | null; onClose: () => void }) {
  return (
    <Drawer open={Boolean(entry)} onClose={onClose} title={entry ? humanize(entry.action) : 'Entry'}>
      {entry && (
        <div className="space-y-5">
          <section>
            <p className="text-sm text-ink">{entry.summary}</p>
            <p className="mt-1 text-xs text-ink-subtle">{formatDateTime(entry.createdAt)}</p>
          </section>

          <section className="rounded-lg border border-line p-3">
            <h3 className="mb-2 text-sm font-semibold text-ink">Who</h3>
            <p className="text-sm text-ink">{entry.actor?.email ?? 'System'}</p>
            {entry.actor?.role && (
              <p className="text-xs text-ink-subtle">{humanize(entry.actor.role)}</p>
            )}
            <p className="mt-2 text-xs text-ink-subtle">
              {entry.ipAddress ? `From ${entry.ipAddress}` : 'No IP address recorded'}
            </p>
            {entry.userAgent && (
              <p className="mt-1 break-words text-xs text-ink-subtle">{entry.userAgent}</p>
            )}
          </section>

          <section className="rounded-lg border border-line p-3">
            <h3 className="mb-2 text-sm font-semibold text-ink">Record</h3>
            <p className="text-sm text-ink">{entry.entity}</p>
            {entry.entityId && (
              <p className="mt-0.5 break-all font-mono text-xs text-ink-subtle">{entry.entityId}</p>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink">
              What changed ({entry.changes.length})
            </h3>

            {entry.changes.length === 0 ? (
              <p className="flex items-start gap-1.5 text-xs text-ink-subtle">
                <ShieldAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                This action recorded no before/after snapshot — sign-ins and exports change nothing.
              </p>
            ) : (
              <div className="space-y-2">
                {entry.changes.map((change) => (
                  <div key={change.field} className="rounded-lg border border-line p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
                      {change.field}
                    </p>
                    <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-ink-subtle">Was</p>
                        <p className="break-words font-mono text-xs text-ink-muted">
                          {render(change.from)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-ink-subtle">Became</p>
                        <p className="break-words font-mono text-xs text-ink">{render(change.to)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

/** Secrets are already redacted at write time; this only has to be readable. */
function render(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value || '""';
  return JSON.stringify(value);
}

/**
 * `statusTone` covers the statuses these actions mirror; the ones it does not
 * know are the destructive ones, which should not read as neutral grey.
 */
function actionTone(action: AuditAction) {
  if (DESTRUCTIVE_ACTIONS.includes(action)) return 'danger' as const;
  if (action === 'SETTINGS_CHANGE' || action === 'ROLE_CHANGE') return 'purple' as const;
  return statusTone(action);
}
